import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Scale, Award, HeartHandshake, ArrowRight } from 'lucide-react';
import PublicLayout from '../components/layout/PublicLayout';
import useSEO from '../hooks/useSEO';

const AboutPage = () => {
    // Dynamically set metadata for SEO optimization
    useSEO({
        title: 'About ScholarsAnchor | Advancing AI for Educational Institutions',
        description: 'Discover ScholarsAnchor, the leading B2B AI SaaS for educational institutions. We advance academic administration and student support through legally-compliant, secure AI.',
        keywords: 'scholars anchor, scholarsanchor, B2B AI SaaS, AI for educational institutions, academic AI, Threnlabs, educational scheduling, secure AI, compliant school administration',
        jsonLd: {
            "@context": "https://schema.org",
            "@type": "AboutPage",
            "name": "About ScholarsAnchor",
            "description": "Learn about the mission, values, and legal compliance standards of ScholarsAnchor as we advance AI B2B SaaS for educational institutions.",
            "publisher": {
                "@type": "Organization",
                "name": "Threnlabs",
                "url": "https://threnlabs.com"
            }
        }
    });

    const values = [
        {
            icon: ShieldCheck,
            title: "Privacy & Security First",
            description: "We implement rigorous data boundaries, ensuring institutional content, syllabus banks, and student logs remain strictly private and compliance-secured under FERPA/GDPR frameworks."
        },
        {
            icon: Scale,
            title: "Legal & Ethical Integrity",
            description: "No exaggerated claims or synthetic data shortcuts. Our mathematical models operate transparently, validating scheduling solutions and doubt-resolution sources legally and traceably."
        },
        {
            icon: Award,
            title: "Academic Excellence",
            description: "Engineered specifically for universities, academies, and school systems to elevate the learning experience while simplifying administrative overhead."
        },
        {
            icon: HeartHandshake,
            title: "Collaborative Innovation",
            description: "We work hand-in-hand with deans, schedulers, and educators to advance AI tools that complement human instruction and institutional governance."
        }
    ];

    return (
        <PublicLayout>
            <div className="relative pt-40 pb-24 px-6 bg-[#0A0A0A] text-white">
                {/* Decorative Mesh Background */}
                <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-indigo-500/5 rounded-full blur-[120px] pointer-events-none"></div>

                <div className="max-w-7xl mx-auto relative z-10">
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-32"
                    >
                        <span className="text-primary font-bold tracking-[0.4em] uppercase mb-4 block text-sm">Our Mission</span>
                        <p className="text-neutral-400 text-xl md:text-2xl max-w-4xl mx-auto font-medium leading-relaxed mt-6">
                            A premier B2B AI SaaS for educational institutions, advancing AI integration with compliance, mathematical optimization, and ultimate safety.
                        </p>
                    </motion.div>

                    {/* Story / Vision Section */}
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center mb-40">
                        <motion.div 
                            initial={{ opacity: 0, x: -30 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="lg:col-span-7 space-y-6"
                        >
                            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight text-white">
                                Anchoring the Future of Educational Infrastructure
                            </h2>
                            <p className="text-neutral-400 text-lg leading-relaxed font-medium">
                                Our platform was established to address the critical gaps in modern school administration. While consumer AI models offer generic responses, educational systems require highly contextual, secure, and verifiable logic.
                            </p>
                            <p className="text-neutral-400 text-lg leading-relaxed font-medium">
                                By delivering a unified platform that integrates advanced timetabling (powered by constraint satisfaction algorithms) and high-fidelity RAG-based doubt resolution, we help institutions manage scheduling complexity, resource allocation, and curriculum coordination.
                            </p>
                            <p className="text-neutral-400 text-lg leading-relaxed font-medium">
                                Our commitment is clear: to advance artificial intelligence for educational institutions legally, ethically, and without compromise.
                            </p>
                        </motion.div>
                        
                        <motion.div 
                            initial={{ opacity: 0, scale: 0.95 }}
                            whileInView={{ opacity: 1, scale: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.6 }}
                            className="lg:col-span-5 relative group"
                        >
                            <div className="absolute -inset-4 bg-indigo-500/10 rounded-[3rem] blur-2xl opacity-50 group-hover:opacity-75 transition duration-1000"></div>
                            <div className="relative p-12 rounded-[3rem] bg-zinc-900/50 border border-white/5 backdrop-blur-md">
                                <div className="text-primary font-black text-6xl mb-6">60%</div>
                                <h3 className="text-xl font-bold text-white mb-3">Administrative Time Saved</h3>
                                <p className="text-neutral-400 text-sm leading-relaxed">
                                    By automating constraint satisfaction problems, our systems allow academic offices to focus on student success instead of calendar conflicts.
                                </p>
                            </div>
                        </motion.div>
                    </div>

                    {/* Values Grid */}
                    <div className="mb-40">
                        <div className="text-left mb-16 max-w-xl">
                            <span className="text-primary font-bold tracking-[0.4em] uppercase mb-4 block text-sm">Core Principles</span>
                            <h2 className="text-3xl md:text-5xl font-black tracking-tighter text-white">How We Operate</h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {values.map((val, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: i * 0.1 }}
                                    className="p-10 rounded-[2.5rem] bg-zinc-900/30 border border-white/5 hover:border-indigo-500/20 transition-all duration-500 group"
                                >
                                    <div className="w-16 h-16 rounded-2xl bg-indigo-500/10 flex items-center justify-center mb-8 border border-indigo-500/20 group-hover:scale-115 transition-transform duration-500">
                                        <val.icon className="w-8 h-8 text-primary" />
                                    </div>
                                    <h3 className="text-2xl font-bold text-white mb-4">{val.title}</h3>
                                    <p className="text-neutral-400 leading-relaxed font-medium">
                                        {val.description}
                                    </p>
                                </motion.div>
                            ))}
                        </div>
                    </div>

                    {/* CTA Section */}
                    <motion.div 
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="p-16 rounded-[3rem] bg-gradient-to-br from-indigo-950/30 to-zinc-900/30 border border-white/5 text-center relative overflow-hidden"
                    >
                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(99,102,241,0.05)_0,transparent_70%)]"></div>
                        <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-6 relative z-10">
                            Partner with Us
                        </h2>
                        <p className="text-neutral-400 text-lg max-w-2xl mx-auto mb-10 relative z-10">
                            Equip your institution with the ultimate AI administration suite and advance your technological edge safely.
                        </p>
                        <div className="flex justify-center relative z-10">
                            <a 
                                href="/signup" 
                                className="bg-white text-black px-10 py-5 rounded-2xl font-bold text-lg hover:shadow-[0_0_30px_rgba(124,93,255,0.4)] transition-all active:scale-95 flex items-center gap-3"
                            >
                                Schedule Institutional Pilot
                                <ArrowRight className="w-5 h-5" />
                            </a>
                        </div>
                    </motion.div>
                </div>
            </div>
        </PublicLayout>
    );
};

export default AboutPage;
